/**
 * Design system tokens — single source of truth.
 * Derived from Figma file HqOl5ilnUGD97v9ahKm06W (Belhop Design System)
 * and creative redesign mockup (belhop-redesign-creative.html).
 *
 * Every visual value used across the app should be imported from here.
 * Token names map to their Figma style IDs in comments for traceability.
 */

// ── Fills ───────────────────────────────────────────────────────────

export const FILLS = {
  /** fill_1TCWVE — page / root background */
  page: '#0D0D0D',
  /** fill_QMRFCA — cards, panels, elevated surfaces */
  surface: '#141414',
  /** fill_9JHHQP — input fields, search bars */
  input: '#1F1F1F',
  /** fill_QDZQCV — slightly elevated surface */
  surfaceRaised: '#101010',
  /** fill_VIUIGF — active/hover surface */
  surfaceActive: '#242424',
  /** fill_1GAT7G — chip/button default fill */
  chip: '#292929',
  /** fill_41A35Q — elevated chip/button */
  chipElevated: '#3A3A3A',
  /** fill_1D8KOV — gold accent fill */
  gold: '#F0B90B',
  /** fill_6RIR98 — warm gold accent */
  goldWarm: '#F9C481',
  /** Gold at 30% opacity — borders, glows */
  goldAlpha30: 'rgba(240, 185, 11, 0.3)',
  /** Gold at 15% opacity — subtle backgrounds */
  goldAlpha15: 'rgba(240, 185, 11, 0.15)',
  /** Gold at 10% opacity — icon boxes */
  goldAlpha10: 'rgba(240, 185, 11, 0.1)',
  /** Gold at 5% opacity — active nav background */
  goldAlpha05: 'rgba(240, 185, 11, 0.05)',
  /** fill_SQNH03 — primary text fill */
  textPrimary: '#ECE9E4',
  /** fill_MRRPBP — muted text fill */
  textMuted: '#918D85',
  /** fill_OM95NE — light neutral text */
  textLight: '#D4CFC7',
  /** fill_47B9FM */
  textPrimaryFaded: 'rgba(236, 233, 228, 0.8)',
  /** fill_QAGRRF — overlay */
  overlay: 'rgba(13, 13, 13, 0.6)',
  /** Lighter overlay — bottom-sheet backdrops */
  overlayLight: 'rgba(13, 13, 13, 0.5)',
  /** Faintest overlay step, below overlayLight (0.5) — scrim edges */
  overlayFaint: 'rgba(13, 13, 13, 0.35)',
  /** Mid-weight overlay — image counter badges, mid-strength scrims */
  overlayMedium: 'rgba(13, 13, 13, 0.7)',
  /** fill_0VGY5X — heavy overlay */
  overlayHeavy: 'rgba(13, 13, 13, 0.8)',
  /** Heavier overlay for badges on images */
  overlayBadge: 'rgba(13, 13, 13, 0.85)',
  /** Near-opaque overlay — full-screen viewer backdrops */
  overlayIntense: 'rgba(13, 13, 13, 0.95)',
  /** fill_GV3U6F — neutral light */
  neutralLight: '#D9D9D9',
  /** fill_F1EGMT — transparent */
  transparent: 'rgba(0, 0, 0, 0)',

  // Status fills
  /** fill_KR3LVU */
  successBg: 'rgba(22, 163, 106, 0.1)',
  /** fill_7W0V4O */
  successBgStrong: 'rgba(22, 163, 106, 0.15)',
  /** fill_CALNX3 */
  success: '#16A36A',
  /** fill_CC6AQ7 */
  successAlt: '#16A34A',
  /** fill_0T4JRJ */
  infoBg: 'rgba(37, 99, 235, 0.1)',
  /** fill_RZ21K1 */
  infoBgStrong: 'rgba(37, 99, 235, 0.15)',
  /** fill_9735IM — info accent. Bumped from #2563EB to #7AB3FF (≈5.7:1 on
   *  surface #161c2a) to clear WCAG 2.0 AA at body text sizes. The original
   *  Tailwind blue-600 was designed for light surfaces and could not meet
   *  4.5:1 on the dark-luxury theme. `infoBg` / `infoBgStrong` keep the
   *  original blue tint — backgrounds don't need the contrast bump. */
  info: '#7AB3FF',
  /** fill_BCNE3I */
  warningBg: 'rgba(240, 185, 11, 0.1)',
  /** fill_ADSLGV */
  warningBgStrong: 'rgba(240, 185, 11, 0.15)',
  /** fill_9BS2UY */
  warningBgMedium: 'rgba(240, 185, 11, 0.2)',
  /** fill_W1FIFR */
  errorBg: 'rgba(206, 41, 41, 0.1)',
  /** fill_0PPGMX */
  errorBgStrong: 'rgba(206, 41, 41, 0.15)',
  /** fill_A7R87D — saturated "Cancelled / destructive" red. Use for
   *  badges, icons, borders, graphical accents. NOT for body text on dark
   *  surfaces — fails WCAG 2.0 AA (≈3.3:1). For text, use `errorText`. */
  error: '#CE2929',
  /** Brighter red for body text on dark backgrounds. ≈7:1 contrast on
   *  `surface`; meets WCAG 2.0 AA. Pair with `errorBg` for the alert pattern.
   *  Added alongside the design-system land — see ADR-0007. */
  errorText: '#F87171',
  /** Dot colour for the bedroom no-photo panel's dotted-surface pattern. */
  surfaceDot: 'rgba(236, 233, 228, 0.06)',
} as const;

// ── Strokes ─────────────────────────────────────────────────────────

export const STROKES = {
  /** stroke_BYP8SZ — default subtle border (buttons, chips, inputs) */
  subtle: '#292929',
  /** Border for solid surfaces lifted above the page (consent panel and banner) */
  elevated: '#3A3A3A',
  /** stroke_0R2M7P — gold card border (featured card) */
  goldCard: 'rgba(240, 185, 11, 0.3)',
  /** stroke_666J0G — muted card border (standard cards) */
  mutedCard: 'rgba(41, 41, 41, 0.5)',
  /** stroke_GUP7P5 — solid gold accent border */
  gold: '#F0B90B',
  /** stroke_HS36W1 — primary text stroke */
  textPrimary: '#ECE9E4',
  /** stroke_XLSFL2 / stroke_EBL7P0 — muted stroke */
  muted: '#918D85',
  /** stroke_GLTIFJ — transparent stroke */
  transparent: 'rgba(0, 0, 0, 0)',
  /** stroke_H1BPC7 / stroke_ZUYRES — directional subtle border */
  subtleDirectional: '#292929',
  /** stroke_WM4M9Q — bottom border subtle */
  subtleBottom: '#292929',
  /** stroke_HQC4ZE — warm gold stroke */
  goldWarm: '#F8C07F',
  /** stroke_XFDIXB — page-colored stroke */
  page: '#0D0D0D',
  /** stroke_E54S6G — light neutral stroke */
  light: '#D4CFC7',
  /** stroke_CTJPSJ — gold card border stronger */
  goldCardStrong: 'rgba(240, 185, 11, 0.4)',

  // Status strokes
  /** stroke_NRYDIQ */
  success: 'rgba(22, 163, 106, 0.3)',
  /** stroke_671JJR */
  info: 'rgba(37, 99, 235, 0.3)',
  /** stroke_1WXRH8 */
  error: 'rgba(206, 41, 41, 0.3)',
} as const;

export const BORDERS = {
  /** Standard subtle control and panel border. */
  subtle: `1px solid ${STROKES.subtle}`,
} as const;

// ── Gradients ───────────────────────────────────────────────────────

/** Channel value of the near-black the text scrim is built from (`rgb(13, 13, 13)`). */
export const PHOTO_TEXT_SCRIM_BASE_CHANNEL = 13;

/** Solid alpha of the near-black band under text drawn on a photo. Sized so Ash
 *  (`FILLS.textLight`, the lightest panel text) holds 4.5:1 over a pure-white pixel
 *  (needs 0.69), with headroom. */
export const PHOTO_TEXT_SCRIM_ALPHA = 0.72;

/** Height over which the text scrim fades from transparent to solid. Text sits below it. */
export const PHOTO_TEXT_SCRIM_FADE_PX = 24;

/** One stop of a vertical scrim: alpha of the near-black at a percentage of the height. */
export interface ScrimStop {
  alpha: number;
  atPercent: number;
}

/** Vertical near-black scrim built from `stops`, on `PHOTO_TEXT_SCRIM_BASE_CHANNEL`. */
export const scrimGradient = (stops: readonly ScrimStop[]): string => {
  const channel = PHOTO_TEXT_SCRIM_BASE_CHANNEL;
  const colourStops = stops
    .map(
      ({ alpha, atPercent }) => `rgba(${channel}, ${channel}, ${channel}, ${alpha}) ${atPercent}%`,
    )
    .join(', ');
  return `linear-gradient(180deg, ${colourStops})`;
};

/** Soft oval of near-black behind a hero's text: constant `alpha` out to `plateauPercent`
 *  of the ellipse radius, falling linearly to 0 at the edge. Percentages are of the box. */
export interface ScrimOval {
  centreXPercent: number;
  centreYPercent: number;
  radiusXPercent: number;
  radiusYPercent: number;
  plateauPercent: number;
  alpha: number;
}

/** A hero or band scrim: a light vertical wash with a soft oval on top. */
export interface HeroScrimConfig {
  wash: readonly ScrimStop[];
  oval: ScrimOval;
}

const scrimRgba = (alpha: number): string =>
  `rgba(${PHOTO_TEXT_SCRIM_BASE_CHANNEL}, ${PHOTO_TEXT_SCRIM_BASE_CHANNEL}, ${PHOTO_TEXT_SCRIM_BASE_CHANNEL}, ${alpha})`;

/** CSS for a hero or band scrim: the oval layer on top of the vertical wash. */
export const heroScrimGradient = ({ wash, oval }: HeroScrimConfig): string => {
  const ovalLayer = `radial-gradient(ellipse ${oval.radiusXPercent}% ${oval.radiusYPercent}% at ${oval.centreXPercent}% ${oval.centreYPercent}%, ${scrimRgba(oval.alpha)} 0%, ${scrimRgba(oval.alpha)} ${oval.plateauPercent}%, ${scrimRgba(0)} 100%)`;
  return `${ovalLayer}, ${scrimGradient(wash)}`;
};

const HERO_WASH_STOPS: readonly ScrimStop[] = [
  { alpha: 0.35, atPercent: 0 },
  { alpha: 0.3, atPercent: 30 },
  { alpha: 0.3, atPercent: 75 },
  { alpha: 0.7, atPercent: 100 },
];
const HERO_OVAL_PLATEAU_PERCENT = 65;
const HERO_OVAL_ALPHA = 0.48;

export const PHOTO_HERO_SCRIM: HeroScrimConfig = {
  wash: HERO_WASH_STOPS,
  oval: {
    centreXPercent: 50,
    centreYPercent: 56,
    radiusXPercent: 90,
    radiusYPercent: 50,
    plateauPercent: HERO_OVAL_PLATEAU_PERCENT,
    alpha: HERO_OVAL_ALPHA,
  },
};
export const DISCOVER_BAND_SCRIM: HeroScrimConfig = {
  wash: HERO_WASH_STOPS,
  oval: {
    centreXPercent: 50,
    centreYPercent: 60,
    radiusXPercent: 90,
    radiusYPercent: 40,
    plateauPercent: HERO_OVAL_PLATEAU_PERCENT,
    alpha: HERO_OVAL_ALPHA,
  },
};
export const LISTING_BAND_SCRIM: HeroScrimConfig = {
  wash: [...HERO_WASH_STOPS.slice(0, 3), { alpha: 0.88, atPercent: 100 }],
  oval: {
    centreXPercent: 50,
    centreYPercent: 48,
    radiusXPercent: 90,
    radiusYPercent: 40,
    plateauPercent: HERO_OVAL_PLATEAU_PERCENT,
    alpha: HERO_OVAL_ALPHA,
  },
};
export const BELHOP_PLUS_BAND_SCRIM: HeroScrimConfig = {
  wash: HERO_WASH_STOPS,
  oval: {
    centreXPercent: 50,
    centreYPercent: 52,
    radiusXPercent: 75,
    radiusYPercent: 40,
    plateauPercent: HERO_OVAL_PLATEAU_PERCENT,
    alpha: 0.52,
  },
};

/** Scrim under the booking confirmation banner's location label and name. The banner is only
 *  about 115px tall in the narrowest mount and its two-line name lifts the label to 19% of the
 *  height, so the text floor is reached by 18% and held to the foot; the top stays light. */
export const BOOKING_BANNER_SCRIM_STOPS: readonly ScrimStop[] = [
  { alpha: 0.3, atPercent: 0 },
  { alpha: PHOTO_TEXT_SCRIM_ALPHA, atPercent: 18 },
  { alpha: 0.85, atPercent: 100 },
];

export const GRADIENTS = {
  /** Text-on-photo band: transparent, rising to a solid near-black over
   *  `PHOTO_TEXT_SCRIM_FADE_PX`, solid below. Separate from the `.card-media` grade. */
  photoTextScrim: `linear-gradient(180deg, rgba(${PHOTO_TEXT_SCRIM_BASE_CHANNEL}, ${PHOTO_TEXT_SCRIM_BASE_CHANNEL}, ${PHOTO_TEXT_SCRIM_BASE_CHANNEL}, 0) 0, rgba(${PHOTO_TEXT_SCRIM_BASE_CHANNEL}, ${PHOTO_TEXT_SCRIM_BASE_CHANNEL}, ${PHOTO_TEXT_SCRIM_BASE_CHANNEL}, ${PHOTO_TEXT_SCRIM_ALPHA}) ${PHOTO_TEXT_SCRIM_FADE_PX}px)`,

  /** fill_6HM0MI — headline text gradient */
  headlineGold:
    'linear-gradient(90deg, rgba(249, 196, 129, 1) 0%, rgba(188, 118, 60, 1) 66%, rgba(252, 233, 176, 1) 100%)',
  /** fill_27FUTP — warm copper gradient */
  copperGold:
    'linear-gradient(90deg, rgba(250, 194, 129, 1) 0%, rgba(236, 176, 113, 1) 23%, rgba(220, 155, 95, 1) 49%, rgba(209, 142, 83, 1) 76%, rgba(239, 199, 142, 1) 97%)',
  /** fill_S3W9CG — vertical surface gradient */
  surfaceVertical:
    'linear-gradient(0deg, rgba(13, 13, 13, 1) 5%, rgba(23, 23, 23, 1) 26%, rgba(27, 27, 27, 1) 49%, rgba(13, 13, 13, 1) 100%)',
  /** fill_H27HFP — diagonal headline gradient */
  headlineGoldDiagonal:
    'linear-gradient(113deg, rgba(249, 196, 129, 1) 45%, rgba(188, 118, 60, 1) 71%, rgba(252, 233, 176, 0.61) 98%)',
  /** stroke_A8LQXK — gradient stroke */
  strokeGold:
    'linear-gradient(90deg, rgba(248, 192, 127, 1) 0%, rgba(232, 170, 108, 1) 21%, rgba(213, 147, 87, 1) 44%, rgba(209, 146, 88, 1) 67%, rgba(239, 200, 142, 1) 96%)',

  /** Creative mockup scrollbar gradient */
  scrollbar: 'linear-gradient(180deg, #F9C481, #885B00)',

  /** Hero radial glow */
  heroRadial:
    'radial-gradient(ellipse 80% 50% at 50% 0%, rgba(240, 185, 11, 0.06) 0%, transparent 60%)',

  /** Layered hero glow — dominant top gold glow plus two faint secondary
   *  glows (copper bottom-left, warm gold mid-right). Used behind the
   *  Discover hero; centralises the previously-inline three-radial stack. */
  heroRadialLayered:
    'radial-gradient(ellipse 80% 50% at 50% 0%, rgba(240, 185, 11, 0.06) 0%, transparent 60%), radial-gradient(ellipse 60% 40% at 20% 80%, rgba(188, 118, 60, 0.04) 0%, transparent 50%), radial-gradient(ellipse 60% 40% at 80% 60%, rgba(249, 196, 129, 0.03) 0%, transparent 50%)',

  /** Shimmer animation gradient for card placeholders */
  shimmer:
    'linear-gradient(110deg, transparent 30%, rgba(249, 196, 129, 0.04) 50%, transparent 70%)',

  /** Primary gold button / FAB / reserve-CTA fill — 135° warm-to-deep gold
   *  (mockup `linear-gradient(135deg, #F9C481, #F0B90B 60%, #BC763C)`). */
  goldButton: 'linear-gradient(135deg, #F9C481 0%, #F0B90B 60%, #BC763C 100%)',

  /** Listing-page hero backdrops (/accommodations & /services). Warm sepia /
   *  warm gold diagonal washes behind the listing hero; both melt into the
   *  page colour. Centralised from the previously-inline HomePage gradients. */
  listingHeroAccommodations: 'linear-gradient(135deg, #1a1510, #2d1f0e 30%, #1a140e 60%, #0D0D0D)',
  listingHeroServices: 'linear-gradient(135deg, #1a1810, #2d250e 30%, #1a180e 60%, #0D0D0D)',
  /** Scrim over the mobile photo hero. See `PHOTO_HERO_SCRIM`. */
  photoHeroScrim: heroScrimGradient(PHOTO_HERO_SCRIM),

  /** Scrim over the desktop Discover band. See `DISCOVER_BAND_SCRIM`. */
  discoverBandScrim: heroScrimGradient(DISCOVER_BAND_SCRIM),

  /** Scrim over the Stays and Services band. See `LISTING_BAND_SCRIM`. */
  listingBandScrim: heroScrimGradient(LISTING_BAND_SCRIM),

  /** Scrim over the Belhop+ band. See `BELHOP_PLUS_BAND_SCRIM`. */
  belhopPlusBandScrim: heroScrimGradient(BELHOP_PLUS_BAND_SCRIM),

  /** Scrim under the booking confirmation banner. See `BOOKING_BANNER_SCRIM_STOPS`. */
  bookingBannerScrim: scrimGradient(BOOKING_BANNER_SCRIM_STOPS),

  // Legacy gradients used by existing components (scrollbar, icon fills)
  gold: 'linear-gradient(180deg, #FBEB80 0%, #EDD160 50%, #885B00 100%)',
  goldHorizontal: 'linear-gradient(90deg, #FBEB80, #885B00)',
  goldVertical: 'linear-gradient(180deg, #FBEB80, #885B00)',
  goldSubtle: 'linear-gradient(135deg, #FBEB80 0%, #EDD160 100%)',
  goldReverse: 'linear-gradient(180deg, #885B00 0%, #EDD160 50%, #FBEB80 100%)',
  goldRadial: 'radial-gradient(circle, #FBEB80 0%, #EDD160 50%, #885B00 100%)',
} as const;

/** Inline style for the booking confirmation banner's `.card-media`: replaces the house
 *  `--image-scrim` with the banner scrim. Spread into `style`, cast to `React.CSSProperties`. */
export const BOOKING_BANNER_MEDIA_VARS = {
  '--image-scrim': GRADIENTS.bookingBannerScrim,
} as const;

export const GRADIENT_STOPS = {
  gold: [
    { offset: '0%', color: '#FBEB80' },
    { offset: '50%', color: '#EDD160' },
    { offset: '100%', color: '#885B00' },
  ],
  goldSimple: [
    { offset: '0%', color: '#FBEB80' },
    { offset: '100%', color: '#885B00' },
  ],
} as const;

// ── Spacing ─────────────────────────────────────────────────────────

/**
 * Shared spacing rhythm — mirrors DESIGN.md's `## Layout` scale
 * (4 / 8 / 12 / 16 / 24 / 32 / 48). Any inline margin, padding or gap
 * between sibling elements should read from here rather than a literal
 * pixel string, so the rhythm stays a single edit.
 */
export const SPACING = {
  xxs: '4px',
  xs: '8px',
  sm: '12px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  xxl: '48px',
} as const;

/**
 * Gap between sibling listing tiles in a grid or rail — 24px columns, 40px
 * rows. The row value is a deliberate, sanctioned off-rhythm step: it does
 * not appear in the `SPACING` scale above. A tile's photo and caption read
 * as one dense block, so the row gap carries more separation than the
 * column gap to keep rows from reading as a single band.
 */
export const LISTING_TILE_GAP = {
  column: SPACING.lg,
  row: '40px',
} as const;

/**
 * The rhythm between a listing section's heading and the tiles below it, and
 * between one section's last caption and the next section's heading. Two more
 * sanctioned off-rhythm values alongside `LISTING_TILE_GAP.row`: desktop's
 * 40px between-sections step (the caption-to-heading distance) and phone's
 * 28px between-rails step (`PhoneRail`'s own bottom margin) — neither appears
 * in the `SPACING` scale. The heading-to-tiles steps reuse `SPACING.md` (16px)
 * and `SPACING.sm` (12px) because those already sit on the rhythm.
 */
export const LISTING_SECTION_SPACING = {
  desktop: { betweenSections: '40px', headingToTiles: SPACING.md },
  phone: { betweenRails: '28px', headingToTiles: SPACING.sm },
} as const;

// ── Radii ───────────────────────────────────────────────────────────

export const RADII = {
  /** Cards, panels, search card */
  card: '12px',
  /** Buttons, filter controls, inputs */
  control: '6px',
  /** Pill-shaped chips, suggestion tags */
  pill: '9999px',
  /** Icon boxes in service cards */
  iconBox: '10px',
  /** Fully round controls — circular icon buttons, avatar frames */
  circle: '50%',
  /** Bottom-sheet top corners */
  sheet: '20px',
} as const;

// ── Sizing ──────────────────────────────────────────────────────────

/** Raw mobile bottom tab-bar content height, before safe-area padding is
 *  added on top by MobileBottomNav. Referenced by both `SIZING.bottomNavHeight`
 *  (the nav's own `minHeight`) and `SIZING.bottomNavOffset` (derived below) so
 *  the two never drift apart. */
const BOTTOM_NAV_CONTENT_HEIGHT = '72px';

/** Side gutter of the phone browse screens: rails, chips, list pages and the feed. */
export const PHONE_PAGE_GUTTER_PX = 20;

export const SIZING = {
  /** Desktop Discover photo band: short enough that the first card row clears
   *  the fold at 1440x900. */
  discoverBandHeight: 'clamp(340px, 42vh, 400px)',
  /** Phone Discover photo band: it ends 272px from the top of a 390x844
   *  phone, 216px under the 56px app bar. */
  phoneFrontDoorHeroHeight: '216px',
  /** Minimum touch target on phones. */
  touchTarget: '44px',
  /** Suggestion pill height */
  pillHeight: '32px',
  /** Filter input / button height */
  controlHeight: '36px',
  /** Nav bar height */
  navHeight: '72px',
  /** Mobile top app-bar height (fixed bar mounted by Header on mobile) */
  mobileAppBarHeight: '56px',
  /** Mobile bottom tab-bar content height (icon + label). Does NOT account
   *  for safe-area padding — MobileBottomNav adds `env(safe-area-inset-bottom)`
   *  on top of this via its own paddingBottom. Use this only as `minHeight` for
   *  the nav itself; any other fixed-bottom element that must clear the nav
   *  should position against `bottomNavOffset` instead. */
  bottomNavHeight: BOTTOM_NAV_CONTENT_HEIGHT,
  /** Safe-area-inclusive offset for fixed-bottom elements that must sit above
   *  the mobile tab bar (e.g. `bottom: SIZING.bottomNavOffset`). Adds
   *  `env(safe-area-inset-bottom)` to the nav's content height so the element
   *  clears the tab bar's home-indicator padding on notched devices; resolves
   *  to the same rendered position as `bottomNavHeight` when the inset is 0. */
  bottomNavOffset: `calc(${BOTTOM_NAV_CONTENT_HEIGHT} + env(safe-area-inset-bottom))`,
  /** Rendered height of the fixed mobile booking bar
   *  (`MobileStickyBooking`/`MobileServiceBooking`): the headline price, the
   *  rate line and the what and when lines with vertical padding each side.
   *  Measured at 128.6px on a service detail page at a 390px viewport with
   *  every line present; set to 132px so the reservation clears the real bar
   *  with a small buffer. Detail pages that
   *  render such a bar reserve `calc(mobileBookingBarHeight + bottomNavOffset)`
   *  as their content's bottom padding so the bar never occludes the last
   *  rows (spec grid, totals, description). */
  mobileBookingBarHeight: '132px',
  /** Outer width of the focus ring `global.css` draws at `:focus-visible`
   *  (a 2px page-coloured inner band plus a 4px gold outer band). The ring is
   *  a box-shadow, so it renders outside the focused element's box: a control
   *  stretched flush inside a parent that clips its overflow needs at least
   *  this much inset, or its ring lands entirely in the clipped region and no
   *  focus indicator is visible at all. */
  focusRingWidth: '4px',
  /** Max content width */
  maxContent: '1280px',
  /** Booking sidebar width */
  bookingSidebar: '380px',
  /** Desktop stay-date overlay width before viewport collision clamping. */
  desktopDateOverlayWidth: '720px',
  /** Viewport clearance around the desktop stay-date overlay. */
  desktopDateOverlayViewportInset: '16px',
  /** Internal padding for the desktop stay-date overlay surface. */
  desktopDateOverlayPadding: '16px',
  /** Close-control padding inside the desktop stay-date overlay. */
  desktopDateOverlayClosePadding: '4px 8px',
  /** Gap between the two desktop calendar months. */
  calendarTwoMonthGap: '20px',
  /** Space below each desktop calendar month heading. */
  calendarMonthLabelMarginBottom: '8px',
  /** Font size for each desktop calendar month heading. */
  calendarMonthLabelFontSize: '14px',
  /** Space below the desktop overlay's range-context header. */
  calendarOverlayHeaderMarginBottom: '16px',
  /** Gap between the full-width detail gallery and the content/rail grid
   *  below it. Also applied as top padding on the rail column so the rail
   *  aligns with the content column's header, which already opens with this
   *  same margin (see `DetailsPageLayout`'s docstring for the coupling). */
  detailGalleryGap: '32px',
  /** Sticky `top` offset for the detail-page booking rail. */
  detailRailStickyTop: '104px',
  /** Column gap between the detail page's content column and its booking rail. */
  detailColumnGap: '40px',
  /** Width-to-height ratio of the desktop gallery bento — a wide landscape
   *  frame that grows with the content width (matches the reference stays
   *  site's gallery, 1535×634). */
  galleryAspectRatio: '2.42 / 1',
  /** Viewport height the desktop gallery always leaves free — the nav, the
   *  gallery-to-header gap, the eyebrow and the title — so on a short window
   *  the listing name stays on screen under the photos. */
  galleryViewportReserve: '225px',
  /** Grid gap between bento tiles at desktop. */
  galleryGap: '8px',
} as const;

/**
 * Mobile stacking order, lowest to highest: cart checkout bar < booking bar < bottom nav <
 * consent banner < (mobile menu and concierge sheets, which use their own literals at
 * 1190 and 1200) < booking sheet < full-screen date step < date picker. The date picker is
 * opened from inside the sheets and the date step, so it must sit above
 * all of them. A new fixed or sticky bottom bar must call `useRegisterBottomChrome`, or the
 * consent banner will cover it.
 */
export const LAYERS = {
  /** Phone Discover chip row, stuck under the app bar. Below the app bar and every sheet. */
  discoverStickyChips: 1050,
  /** Sticky mobile booking bar (stay and service) — below the bottom nav. */
  mobileBookingBar: 1000,
  /** Sticky cart checkout bar — below the mobile bottom tab bar. */
  cartCheckoutBar: 22,
  /** Mobile bottom tab bar. */
  mobileBottomNav: 1100,
  /** Cookie consent banner: above the bars it sits over, below every sheet and menu. */
  consentBanner: 1150,
  /** Landing page search popovers (desktop): above the consent banner so their lowest controls stay clickable. */
  heroSearchPopover: 1160,
  /** Full-screen date step inside the mobile sticky booking sheet. */
  mobileFullScreenDateStep: 1400,
  /** Backdrop behind the mobile booking sheets; above the bottom nav. */
  mobileBookingSheetBackdrop: 1300,
  /** Mobile booking sheet panel above its backdrop. */
  mobileBookingSheet: 1301,
  /** Mobile date-picker backdrop; above the full-screen date step. */
  customerDatePickerBackdrop: 1500,
  /** Portalled desktop date picker (no backdrop is rendered on desktop). */
  customerDatePickerOverlay: 1001,
  /** Mobile date-picker sheet above its backdrop. */
  customerDatePickerSheet: 1501,
  /** Sign-in dialog backdrop; above every sheet and date picker it can open over. */
  authPromptBackdrop: 1600,
  /** Sign-in dialog panel above its backdrop. */
  authPrompt: 1601,
} as const;

// ── Typography ──────────────────────────────────────────────────────

export const FONTS = {
  /** Display headings — Manrope, light weights + tight tracking */
  display: "'Manrope', system-ui, sans-serif",
  /** Headings, section titles — sans-serif */
  heading: "'Schibsted Grotesk', sans-serif",
  /** Body text, UI labels, card text */
  body: "'Schibsted Grotesk', sans-serif",
  /** Monospace — code, slugs, technical captions */
  mono: "'ui-monospace', 'SF Mono', 'Menlo', monospace",
} as const;

/**
 * Booking-flow type roles: one role maps to one size and weight. Spread a
 * role into a style object; the face comes from the role, so a component
 * never writes a literal font size or weight. A role is added when the first
 * component reads it.
 */
export const TYPE = {
  /** Step and confirmation titles. */
  sheetTitle: { fontFamily: FONTS.display, fontSize: '20px', fontWeight: 500 },
  /** A listing name inside a summary or an offer row. */
  itemName: { fontFamily: FONTS.display, fontSize: '15px', fontWeight: 500 },
  /** A sheet footer total and the confirmation's price. */
  priceTotal: { fontFamily: FONTS.heading, fontSize: '20px', fontWeight: 600 },
  /** Every booking call to action. */
  cta: { fontFamily: FONTS.body, fontSize: '15px', fontWeight: 600 },
  /** The headline price in a booking bar or rail. */
  priceHeadline: {
    fontFamily: FONTS.body,
    fontSize: '24px',
    fontWeight: 400,
    fontVariantNumeric: 'lining-nums',
  },
  /** Long-form reading copy: the blog post body. */
  readingBody: { fontFamily: FONTS.body, fontSize: '17px', fontWeight: 400, lineHeight: 1.7 },
  /** Default body copy: a muted idle note, a struck-through original price. */
  body: { fontFamily: FONTS.body, fontSize: '15px', fontWeight: 400 },
  /** Body copy that carries meaning: a muted explanation line. */
  secondary: { fontFamily: FONTS.body, fontSize: '14px', fontWeight: 400 },
  /** A bold secondary line: an extras heading, a retry link. */
  secondaryStrong: { fontFamily: FONTS.body, fontSize: '14px', fontWeight: 600 },
  /** Meta lines: the confirmation's dates and party, charge-timing sentences. */
  meta: { fontFamily: FONTS.body, fontSize: '13px', fontWeight: 400 },
  /** A tappable summary pill, such as a "Change dates" chip. */
  chip: { fontFamily: FONTS.body, fontSize: '13px', fontWeight: 500 },
  /** Status, helper, error and disclosure lines. */
  finePrint: { fontFamily: FONTS.body, fontSize: '12px', fontWeight: 400 },
  /** Micro-labels: group names, tile labels, field labels. */
  label: {
    fontFamily: FONTS.body,
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
} as const;

// ── Imagery ─────────────────────────────────────────────────────────

/**
 * Art-direction layer for photography the platform does not control.
 * Supplier images arrive at arbitrary exposure, saturation and colour
 * temperature; applied uniformly, these pull them into the dark-gold
 * world so a mixed grid reads as one commissioned set rather than an
 * assembled feed. Consumed by `.card-image` / `.card-media` in
 * global.css — components should not apply these inline.
 */
export const IMAGERY = {
  /** Resting treatment — desaturated, deepened, faintly warmed. */
  treatment: 'saturate(0.74) contrast(1.08) brightness(0.86) sepia(0.12)',
  /** Hover — the photograph wakes to full colour under attention. */
  treatmentActive: 'saturate(1) contrast(1.02) brightness(1) sepia(0)',
  /** Vertical scrim: keeps overlay pills legible at the top edge and melts
   *  the image into the card body at the bottom instead of cutting hard. */
  scrim:
    'linear-gradient(180deg, rgba(13, 13, 13, 0.42) 0%, rgba(13, 13, 13, 0) 38%, rgba(13, 13, 13, 0) 58%, rgba(20, 20, 20, 0.78) 100%)',
  /** Top-only scrim for photographs shown in full colour: keeps the badge and
   *  heart legible and leaves the rest of the photo untouched. */
  scrimTop: 'linear-gradient(180deg, rgba(13, 13, 13, 0.42) 0%, rgba(13, 13, 13, 0) 30%)',
} as const;

/**
 * Frosted label drawn on a listing photo: a glyph and a sentence-case word.
 * Consumed by `PhotoBadge`.
 */
export const PHOTO_BADGE = {
  blur: 'blur(8px)',
  fontSize: '13px',
  fontWeight: 500,
  glyphSize: 14,
  gap: '5px',
  padding: '5px 11px 5px 9px',
  inset: '10px',
  /** Leaves room for the 32px heart (8px in) on the opposite corner. */
  maxWidth: 'calc(100% - 56px)',
} as const;

// ── Shadows ─────────────────────────────────────────────────────────

export const SHADOWS = {
  /** Accommodation card hover */
  cardHover: '0 20px 60px rgba(0,0,0,0.4), 0 0 0 1px rgba(240,185,11,0.3)',
  /** Service card hover */
  serviceHover: '0 16px 48px rgba(0,0,0,0.3)',
  /** Chat panel */
  chatPanel: '0 24px 80px rgba(0,0,0,0.6)',
  /** Admin table row hover */
  tableRowHover: '0 4px 24px rgba(0,0,0,0.2)',
  /** Cookie consent banner floating over the page */
  consentBanner: '0 8px 32px rgba(0,0,0,0.5)',
  /** Centred dialog floating over a dimmed page */
  dialog: '0 32px 80px rgba(0,0,0,0.7)',
} as const;

// ── Glassmorphism ───────────────────────────────────────────────────

export const GLASS = {
  /** Light blur behind a centred dialog's dimmed backdrop. */
  backdropBlur: 'blur(4px)',
  nav: {
    background: 'rgba(13, 13, 13, 0.8)',
    blur: 'blur(24px) saturate(1.2)',
    border: `1px solid #292929`,
  },
  chat: {
    background: 'rgba(20, 20, 20, 0.85)',
    blur: 'blur(32px)',
    border: `1px solid rgba(240, 185, 11, 0.15)`,
  },
  /** The gallery's "Show all N photos" control — reuses the existing
   *  overlay-badge and gold-alpha-30 fills rather than duplicating their
   *  literal values. */
  photoControl: {
    background: FILLS.overlayBadge,
    blur: 'blur(8px)',
    border: `1px solid ${FILLS.goldAlpha30}`,
  },
} as const;

/**
 * Cream search layer: the search surfaces (the desktop bar and popovers, the
 * phone trip-search sheet) float over a photograph or open above the page, so
 * they take a light surface with ink text. Booking surfaces stay dark.
 * Mirrored as `--search-*` custom properties in global.css.
 */
export const SEARCH_LAYER = {
  surface: '#ECE9E4',
  ink: '#0D0D0D',
  inkSecondary: '#3E3D3C',
  inkMuted: '#5F5E5C',
  card: '#E1DED9',
  hover: '#E1DED9',
  input: '#F9F8F7',
  inputPlaceholder: '#6B6B6B',
  stroke: '#D1CFCA',
  /** Search copper: gold used as text or an icon on cream (gold itself is 1.49:1). */
  accent: '#8C5426',
} as const;

/** The surface a shared search primitive draws on; booking surfaces are `'dark'`. */
export type SurfaceTone = 'dark' | 'cream';
